import { Box, Flex, Image, Link, Spacer, Text } from "@chakra-ui/react";
import React from "react";
import { ManagerVerificationStatusEnumType } from "../../../api/enums";
import { buildMailTo } from "../../../utils/email";
import { replaceImage } from "../../../utils/images";
import { isManagerVerified } from "../../../utils/managers";
import { Button } from "../../common";
import { ManagerLogo } from "../../common/managers/ManagerLogo";
import ManagerStatusIconWithTooltip, {
  MANAGER_STATUS_DATA,
} from "../../common/managers/ManagerStatusWithTooltip/ManagerStatusWithTooltip";
import { Card } from "../../dashboard";
import {
  ManagerProfileEditingSection,
  useManagerProfile,
} from "../ManagerProfileProvider";
import { ManagerBannerEditor } from "./ManagerBannerEditor";
import { ManagerProfileCoverProps } from "./ManagerProfileCover.types";
export const ManagerProfileCover: React.FC<ManagerProfileCoverProps> = ({
  manager,
  onClickVerity,
  refreshManager,
  isCompact,
  ...rest
}) => {
  const isVerified = isManagerVerified(manager);
  const { isEditor, editingDrawer, setEditingDrawer } = useManagerProfile(true);

  const bannerImages =
    manager.bannerImages?.filter(
      (images) => images.layout === manager.bannerLayout,
    ) || [];

  const status =
    manager.verificationStatus || ManagerVerificationStatusEnumType.Unverified;

  const handleTooltipClick = () => {
    if (!isVerified) {
      onClickVerity?.();
    }
  };

  const coverHeight = "sm:h-[370px]";

  if (isEditor && editingDrawer === ManagerProfileEditingSection.BannerImages) {
    return <ManagerBannerEditor refreshManager={refreshManager} />;
  }

  return (
    <Box className={"relative overflow-hidden rounded-lg mb-4 sm:mb-0"}>
      <Flex
        backgroundSize={"cover"}
        className={`w-full absolute inset-0 flex items-end overflow-hidden flex-col sm:flex-row ${coverHeight}`}
        {...rest}
      >
        {bannerImages.map((image, imageIndex) => (
          <Box key={imageIndex}>
            <Image
              className={"sm:min-h-[370px]"}
              width={"100%"}
              objectFit={"cover"}
              src={replaceImage(
                image.croppedImage?.url || "",
                `${Math.round(1200 / bannerImages.length)}xAUTO`,
              )}
            />
          </Box>
        ))}
      </Flex>
      <Flex
        className={`${coverHeight} relative px-7 py-8 sm:py-12`}
        bg={
          "linear-gradient(359.01deg, rgba(0, 0, 0, 0.75) 0.89%, rgba(0, 0, 0, 0) 110.13%)"
        }
      >
        <Flex
          className={
            "flex-col justify-start items-start sm:flex-row sm:items-end w-full"
          }
        >
          <ManagerLogo
            border={"1px solid #C9CDD6"}
            size={75}
            p={1}
            bg={"white"}
            mr={3}
            manager={manager}
            onClick={
              isEditor
                ? setEditingDrawer.bind(null, ManagerProfileEditingSection.Logo)
                : undefined
            }
            className={"mb-4 sm:mb-0"}
            _hover={isEditor ? { cursor: "pointer" } : undefined}
          />
          <Box className={"flex-grow"}>
            {isVerified && (
              <Box
                mb={3.5}
                borderRadius={4}
                background={MANAGER_STATUS_DATA[status].bgColor}
                px={2}
                py={"px"}
                flexDirection={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                display={"inline-flex"}
                width={"auto"}
                cursor={"pointer"}
              >
                <ManagerStatusIconWithTooltip
                  status={status}
                  textOnly={false}
                  childrenOnly={false}
                  onTooltipClick={handleTooltipClick}
                  tooltipIsOpenByDefault={!isVerified && !isCompact}
                >
                  <Box ml={2} display={"inline-block"}>
                    <Text
                      textStyle={"bodySmallBold"}
                      color={MANAGER_STATUS_DATA[status].textColor}
                    >
                      {MANAGER_STATUS_DATA[status].header}
                    </Text>
                  </Box>
                </ManagerStatusIconWithTooltip>
              </Box>
            )}
            <Box textStyle={"h1"} lineHeight={"32px"} color={"white"}>
              {isEditor ? (
                <Flex w={"100%"}>
                  <Box>{manager.name}</Box>
                  <Box ml={2} flexGrow={1}>
                    <Button
                      onClick={setEditingDrawer.bind(
                        null,
                        ManagerProfileEditingSection.BasicInfo,
                      )}
                      isFlat
                    >
                      Edit
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      onClick={setEditingDrawer.bind(
                        null,
                        ManagerProfileEditingSection.BannerImages,
                      )}
                      isFlat
                    >
                      Edit
                    </Button>
                  </Box>
                </Flex>
              ) : (
                manager.name
              )}
            </Box>
          </Box>
          <Spacer />
          {manager.contactEmail && (
            <Card px={4} py={4} bg={"#FAFAFA"}>
              <Text textStyle={"bodyRegularBold"}>
                Contact Investment Manager
              </Text>
              <Link
                color={"light.main"}
                href={buildMailTo(manager.supportEmail || "")}
              >
                {manager.supportEmail}
              </Link>
            </Card>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
